<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SLAFilter from '../SLA/SLAFilter.vue';
import ReportFilterPanel from '../ReportFilterPanel.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { subDays, fromUnixTime } from 'date-fns';
import { reportPeriodUnix } from 'dashboard/helper/reportPeriod';
import { useReportTimezone } from 'dashboard/composables/useReportTimezone';
import {
  generateReportURLParams,
  parseReportURLParams,
  parseFilterURLParams,
  generateFilterURLParams,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { useI18n } from 'vue-i18n';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useShellPeriod } from '../../composables/useShellPeriod';
import { rangeLabelKey } from '../../helpers/reportsPeriod';

const props = defineProps({
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['filterChange', 'retry']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isWorkspaceV2 } = useWorkspaceV2();
// Dentro da casa dos relatorios o periodo vem do seletor unico da casca. Esta
// tela ja segue a URL (watch de route.query abaixo); aqui so some o calendario.
const shellPeriod = useShellPeriod();

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 6), new Date()];

const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS);
const filtersFromURL = () => {
  const filters = parseFilterURLParams(route.query);
  return {
    assigned_agent_id: filters.agent_id,
    inbox_id: filters.inbox_id,
    team_id: filters.team_id,
    sla_policy_id: filters.sla_policy_id,
    label_list: filters.label,
  };
};
const selectedGroupByFilter = ref(filtersFromURL());
const customDateRange = ref(initialDateRange);
const filterResetKey = ref(0);
const initialized = ref(false);

// Bordas do periodo no fuso da CONTA, nao no do navegador.
const { reportTimezone } = useReportTimezone();
const reportPeriod = computed(() =>
  reportPeriodUnix({
    dates: customDateRange.value,
    rangeType: selectedDateRange.value,
    timeZone: reportTimezone.value,
  })
);
const to = computed(() => reportPeriod.value.to);
const from = computed(() => reportPeriod.value.from);

const updateURLParams = () => {
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
  });

  const params = {
    ...dateParams,
    ...generateFilterURLParams({
      agent_id: selectedGroupByFilter.value?.assigned_agent_id,
      inbox_id: selectedGroupByFilter.value?.inbox_id,
      team_id: selectedGroupByFilter.value?.team_id,
      sla_policy_id: selectedGroupByFilter.value?.sla_policy_id,
      label: selectedGroupByFilter.value?.label_list,
    }),
  };

  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    ...selectedGroupByFilter.value,
  });
};

const emitFilterChange = params => {
  selectedGroupByFilter.value = params;
  if (!initialized.value) return;
  emit('filterChange', {
    from: from.value,
    to: to.value,
    ...selectedGroupByFilter.value,
  });
};

const activeFilterCount = computed(
  () => Object.values(selectedGroupByFilter.value || {}).filter(Boolean).length
);

const selectedRangeLabel = computed(() =>
  t(rangeLabelKey(selectedDateRange.value))
);

const filterContext = computed(() =>
  activeFilterCount.value
    ? t('REPORT.FILTER_PANEL.CONTEXT_WITH_FILTERS', {
        range: selectedRangeLabel.value,
        count: activeFilterCount.value,
      })
    : selectedRangeLabel.value
);

const canReset = computed(
  () =>
    activeFilterCount.value > 0 ||
    selectedDateRange.value !== DATE_RANGE_TYPES.LAST_7_DAYS
);

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType;
  emitChange();
};

const setInitialRange = () => {
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  emitChange();
};

const resetFilters = async () => {
  selectedGroupByFilter.value = null;
  selectedDateRange.value = DATE_RANGE_TYPES.LAST_7_DAYS;
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  await router.replace({
    query: generateReportURLParams({
      from: from.value,
      to: to.value,
      range: selectedDateRange.value,
    }),
  });
  filterResetKey.value += 1;
  emit('filterChange', {
    from: from.value,
    to: to.value,
    assigned_agent_id: null,
    inbox_id: null,
    team_id: null,
    sla_policy_id: null,
    label_list: null,
  });
};

watch(
  () => route.query,
  () => {
    const dates = parseReportURLParams(route.query);
    const nextDates =
      dates.from && dates.to
        ? [fromUnixTime(dates.from), fromUnixTime(dates.to)]
        : [subDays(new Date(), 6), new Date()];
    const nextRange = dates.range || DATE_RANGE_TYPES.LAST_7_DAYS;
    const nextPeriod = reportPeriodUnix({
      dates: nextDates,
      rangeType: nextRange,
      timeZone: reportTimezone.value,
    });
    const filters = filtersFromURL();
    const sameFilters = Object.keys(filters).every(
      key => filters[key] === (selectedGroupByFilter.value?.[key] ?? null)
    );
    if (
      sameFilters &&
      nextRange === selectedDateRange.value &&
      nextPeriod.from === from.value &&
      nextPeriod.to === to.value
    )
      return;
    customDateRange.value = nextDates;
    selectedDateRange.value = nextRange;
    if (!sameFilters) filterResetKey.value += 1;
    emitFilterChange(filters);
  }
);

onMounted(() => {
  initialized.value = true;
  if (!route.query.from || !route.query.to) {
    setInitialRange();
  } else {
    emitFilterChange(selectedGroupByFilter.value);
  }
});
</script>

<template>
  <ReportFilterPanel
    :context="filterContext"
    :can-reset="canReset"
    :error-message="props.errorMessage"
    @reset="resetFilters"
    @retry="emit('retry')"
  >
    <div
      class="flex flex-col flex-wrap w-full gap-3 md:flex-row"
      :class="{ 'md:items-end': isWorkspaceV2 }"
    >
      <label
        v-if="!shellPeriod"
        :class="
          isWorkspaceV2
            ? 'flex flex-col gap-1.5 text-xs font-medium text-n-slate-11'
            : 'contents'
        "
      >
        <span v-if="isWorkspaceV2">{{ $t('REPORT.FILTER_PANEL.PERIOD') }}</span>
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
      </label>
      <SLAFilter
        :key="filterResetKey"
        :emit-initial="false"
        @filter-change="emitFilterChange"
      />
    </div>
  </ReportFilterPanel>
</template>
